import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Workflow, GitMerge, Sparkles, ListChecks, Database, LineChart } from "lucide-react";
import Seo from "@/components/site/Seo";
import JsonLd from "@/components/site/JsonLd";
import BookCall from "@/components/site/BookCall";

/**
 * Problem-led grouping. Visitors don't shop for "managed services" — they
 * arrive because something specific in their setup is broken. We name the
 * problem first, then map the relevant services to it.
 */
const PROBLEM_GROUPS = [
  {
    icon: Sparkles,
    problem: "Campaigns are taking too long to launch.",
    detail:
      "Intake is informal, QA is whoever has time, launches slip. We standardize the campaign build pipeline so a new request becomes a clean launch on a predictable schedule.",
    services: [
      { to: "/services/campaign-operations", label: "Campaign Operations" },
      { to: "/marketo-services", label: "Adobe Marketo Engage Services" },
      { to: "/services/managed-services", label: "Managed Marketing Operations" },
    ],
  },
  {
    icon: GitMerge,
    problem: "Marketo and Salesforce don't agree on lead status.",
    detail:
      "Sync errors, picklist drift, lifecycle stages defined three different ways, and routing that nobody trusts. We rewire the Marketo–Salesforce relationship so the same lead reads the same in both systems.",
    services: [
      { to: "/services/salesforce-crm", label: "Salesforce & CRM Operations" },
      { to: "/marketo-services", label: "Marketo CRM Sync" },
      { to: "/services/martech-integration", label: "Lifecycle & Lead Routing" },
    ],
  },
  {
    icon: LineChart,
    problem: "Reports don't tie back to pipeline.",
    detail:
      "UTM hygiene is loose, source fields are noisy, and two dashboards tell two different stories. We rebuild the source-field architecture and reporting layer so marketing-sourced and influenced pipeline are real numbers.",
    services: [
      { to: "/services/reporting-attribution", label: "Reporting & Attribution" },
      { to: "/services/martech-integration", label: "Martech & Data Integration" },
      { to: "/services/salesforce-crm", label: "Campaign Reporting in Salesforce" },
    ],
  },
  {
    icon: Workflow,
    problem: "The Marketo instance has drifted.",
    detail:
      "Hundreds of programs, no shared naming, scoring nobody calibrates, lifecycle that quietly stopped working. We run a full instance review and rebuild what's worth keeping — without a rip-and-replace.",
    services: [
      { to: "/marketo-services", label: "Marketo Instance Audit" },
      { to: "/marketo-services", label: "Program Templates & Governance" },
      { to: "/marketo-services", label: "Lead Scoring & Lifecycle" },
    ],
  },
  {
    icon: Database,
    problem: "The stack is connected but nothing is really aligned.",
    detail:
      "Fields differ between systems, consent capture is patchy, dedupe is manual, and UTMs are a free-for-all. We standardize the data plumbing so the systems downstream are usable.",
    services: [
      { to: "/services/martech-integration", label: "Martech & Data Integration" },
      { to: "/services/reporting-attribution", label: "UTM & Source Taxonomy" },
      { to: "/marketo-services", label: "Database Hygiene & Segmentation" },
    ],
  },
  {
    icon: ListChecks,
    problem: "The MOPs team is buried and execution is slipping.",
    detail:
      "Tickets win the day, strategy work gets pushed, and senior thinking shows up only on Fridays. We embed a senior operator for ongoing capacity, QA, and platform upkeep — without the agency-handoff theatre.",
    services: [
      { to: "/services/managed-services", label: "Managed Marketing Automation" },
      { to: "/services/campaign-operations", label: "Campaign Operations Support" },
      { to: "/marketo-services", label: "Fractional Senior MOPs" },
    ],
  },
];

export default function Services() {
  return (
    <>
      <Seo
        title="Services | Marketo, Salesforce, Campaign Ops, Reporting — Xadentra"
        description="Problem-led B2B marketing automation services — Marketo/Salesforce sync, lifecycle, lead routing, campaign operations, managed support, and reporting."
        path="/services"
      />
      <JsonLd
        id="services-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://xadentra.com/" },
            { "@type": "ListItem", position: 2, name: "Services", item: "https://xadentra.com/services" },
          ],
        }}
      />

      {/* HERO */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob" style={{ width: 480, height: 480, background: "#6D4DFF", top: -180, left: -100, opacity: 0.55 }} />
        <div className="xa-blob" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -120, right: -120, opacity: 0.35 }} />
        <div className="absolute inset-0 xa-grid-dark opacity-50 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32 relative">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#00D4FF]">Services</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight leading-[1.1] sm:leading-[1.05] lg:leading-[0.98] max-w-5xl">
            We work on the specific things that are breaking your funnel.
          </h1>
          <p className="mt-8 text-lg text-white/70 max-w-3xl leading-relaxed">
            Find the problem that sounds like your current setup. That's where we start. Each
            engagement is scoped from a written assessment — not a generic retainer.
          </p>
        </div>
      </section>

      {/* PROBLEM GROUPS */}
      <section className="bg-[#F7F9FC] border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 space-y-6">
          {PROBLEM_GROUPS.map((g, i) => (
            <div
              key={i}
              data-testid={`services-problem-${i}`}
              className="bg-white border border-[#E5E7EB] rounded-2xl p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 hover:border-[#6D4DFF]/40 hover:shadow-[0_24px_60px_-30px_rgba(7,17,31,0.18)] transition-all"
            >
              <div className="lg:col-span-5">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#6D4DFF]/12 to-[#00D4FF]/12 grid place-items-center text-[#6D4DFF]">
                    <g.icon className="w-5 h-5" />
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6D4DFF]">
                    Problem · {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <h2 className="font-display mt-6 text-2xl lg:text-3xl font-bold tracking-tight text-[#07111F] leading-snug">
                  {g.problem}
                </h2>
              </div>
              <div className="lg:col-span-7">
                <p className="text-[#374151] leading-relaxed">{g.detail}</p>
                <div className="mt-7">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#6B7280] mb-3">
                    What we'd use to fix it
                  </div>
                  <ul className="flex flex-wrap gap-2">
                    {g.services.map((s, j) => (
                      <li key={j}>
                        <Link
                          to={s.to}
                          data-testid={`services-problem-${i}-svc-${j}`}
                          className="inline-flex items-center gap-2 px-3.5 py-2 text-sm text-[#07111F] bg-[#F7F9FC] border border-[#E5E7EB] rounded-full hover:border-[#6D4DFF]/50 hover:text-[#6D4DFF] transition"
                        >
                          {s.label} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#07111F] text-white relative overflow-hidden">
        <div className="xa-blob pointer-events-none" style={{ width: 460, height: 460, background: "#6D4DFF", top: -160, left: -120, opacity: 0.4 }} />
        <div className="xa-blob pointer-events-none" style={{ width: 380, height: 380, background: "#00D4FF", bottom: -140, right: -100, opacity: 0.25 }} />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end relative">
          <div className="lg:col-span-8">
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">
              Not sure which problem you actually have?
            </h2>
            <p className="mt-5 text-white/70 max-w-2xl">
              That's a great call to have. Book a 30-minute working session — a senior
              specialist will look at your setup and tell you the lowest-effort fix.
            </p>
          </div>
          <BookCall source="services_cta" className="lg:col-span-4 lg:justify-self-end w-fit">
            Book a meeting
          </BookCall>
        </div>
      </section>
    </>
  );
}
